import { buildApiUrl, tenantApiPrefix } from '../config';
import { getTenantIdFromStorage } from '../utils/onboardingLocalStorage';

/**
 * The workspace's agents — ROCCO and the rest — provisioned from the backend
 * like manual dashboards. Listed with a few figures; renamed, re-described or
 * deleted by an admin.
 */
export interface TenantAgent {
  id: string;
  /** What it is: "rocco", "policy_servicing", … Also its address: /agents/rocco. */
  kind: string;
  name: string;
  description: string | null;
  category: string | null;
  /** Where it sits in a policy's journey: "intake", "quote", "bind_issue" or "service". The Agents page groups by it. */
  stage?: string | null;
  icon: string | null;
  /** False for a kind whose screens are not built yet: listed, not opened. */
  ready: boolean;
  created_at: string;
  /** Sections its settings leave empty, not shown in its sidebar: ["accounts"] for a desk with no employer accounts. */
  hidden_sections?: string[];
  /** Switched on: its background work runs. Off: nothing runs, but its screens still open. */
  on?: boolean;
  paused_at?: string | null;
}

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('klaris_jwt');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

function requireTenantPrefix(): string {
  const tid = getTenantIdFromStorage();
  if (!tid) throw new Error('Not authenticated');
  return tenantApiPrefix(tid);
}

async function send<T>(path: string, init: RequestInit, fallbackMessage: string): Promise<T> {
  const response = await fetch(buildApiUrl(`${requireTenantPrefix()}/tenant-agents${path}`), {
    ...init,
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const detail = body && typeof body === 'object' ? (body as { detail?: unknown }).detail : null;
    throw new Error(typeof detail === 'string' ? detail : fallbackMessage);
  }
  return response.status === 204 ? (undefined as T) : response.json();
}

export const fetchTenantAgents = () =>
  send<{ agents: TenantAgent[] }>('', { method: 'GET' }, 'Could not load agents').then((r) => r.agents);

export const updateTenantAgent = (id: string, change: { name?: string; description?: string; on?: boolean }) =>
  send<TenantAgent>(`/${id}`, { method: 'PATCH', body: JSON.stringify(change) }, 'Could not save the agent');

export const deleteTenantAgent = (id: string) =>
  send<void>(`/${id}`, { method: 'DELETE' }, 'Could not delete the agent');

/** Something waiting on a person: drafts to check, files to confirm, or a job that stopped. */
export interface AgentNeed {
  /** How many; empty for a problem rather than a pile of work. */
  count: number | null;
  label: string;
  /** The agent's page that shows them: "live", "quotes", "integrations". */
  section: string;
  agent_id: string;
  agent_name: string;
  problem?: boolean;
}

/** One agent at work, read from its own records. */
export interface AgentPulse {
  /** Being worked on now. */
  working: number;
  /** Finished in the period, in the agent's own words: "emails answered". */
  done: number;
  done_label: string;
  /** Handed to a person in the period, each with its reason. */
  handed: number;
  /** Calls made, emails and WhatsApp messages sent, in the period. */
  contacts: number;
  needs: { count: number; label: string; section: string }[];
  /** Said when its background job last failed; open the agent to see more. */
  problem: string | null;
}

export interface AgentActivity {
  text: string;
  at: string;
  section: string;
  agent_id: string;
  agent_name: string;
}

export interface AgentsOverview {
  days: number;
  needs: AgentNeed[];
  agents: (TenantAgent & { pulse: AgentPulse | null })[];
  activity: AgentActivity[];
}

export const fetchAgentsOverview = (days: number) =>
  send<AgentsOverview>(`/overview?days=${days}`, { method: 'GET' }, 'Could not load what the agents are doing');
